import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

import { MacroGroupsService } from '@app/services/net-remit/macro-groups.service';
import { CustomerService } from '@app/services/net-remit/customer.service';

import { CustomerEntityView } from '@app/shared/models/CustomerEntityView';
import { FilesInputView } from '@app/shared/models/FilesInputView';

import { fadeIn } from 'assets/animations/animations';
import { finalize, takeWhile } from 'rxjs/operators';

@Component({
    selector: 'app-create-macro-groups-form',
    templateUrl: './create-macro-groups-form.component.html',
    styleUrls: ['./create-macro-groups-form.component.scss'],
    animations: [fadeIn],
    standalone: false
})
export class CreateMacroGroupsFormComponent implements OnInit, OnDestroy {
  alive: boolean = true;

  // Values selected
  customerSelected: CustomerEntityView;
  selectedFile: FilesInputView;
  // List
  customerList: Array<CustomerEntityView>;

  // Loading
  isLoading: boolean;
  isLoadingCustomer: boolean = true;

  @ViewChild('file') file: any;

  constructor(
    private primeMessageService: MessageService,
    private translate: TranslateService,
    private customerService: CustomerService,
    private macroGroupsService: MacroGroupsService
  ) { }

  ngOnInit(): void {
    // All active customers with GNR5 product (airline and TA type), plus accelya customer
    this.customerService.findAllCustomers({onlyGNR5: true, onlyActive: true, addAccelya: true})
    .pipe(finalize(() => { this.isLoadingCustomer = false }), takeWhile(() => this.alive))
    .subscribe((customers: Array<CustomerEntityView>) => {
      this.customerList = customers;
    });
  }

  public selectFile(event) {
    const isValidFile = event.currentFiles.length > 0;
    const inputFile = event.files[0];
    if (isValidFile && inputFile?.size > 0) {
      const reader = new FileReader();
      const scope = this;
      reader.onload = function() {
        scope.selectedFile = new FilesInputView(inputFile.name, window.btoa(reader.result.toString()), inputFile.size);
      }
      reader.readAsBinaryString(inputFile);
    } else if(isValidFile && inputFile?.size == 0) {
      this.deleteFile();

      this.primeMessageService.add({
        sticky: true,
        severity:'error',
        summary: this.translate.instant('Modal.Error'),
        detail: this.translate.instant('Selected File Is Empty')
      });
    } else if(!isValidFile){
      this.selectedFile = new FilesInputView(inputFile.name, null, 0);
    }
  }

  deleteFile() {
    this.file.clear();
    this.selectedFile = undefined;
  }

  saveMacroGroupFile() {
    this.isLoading = true;
    this.macroGroupsService.uploadMacroGroups(this.customerSelected.customerId, this.selectedFile.file)
      .pipe(finalize(() => { this.isLoading = false }), takeWhile(() => this.alive))
      .subscribe({ next: () => {
        this.primeMessageService.add({
          sticky: true,
          severity:'success',
          summary: this.translate.instant('Modal.Success'),
          detail: this.translate.instant('MacroGroups Load Success', {fileName: this.selectedFile.filename})
        });

        this.selectedFile = undefined;
        this.customerSelected = undefined;
      }, error: (errors: HttpErrorResponse) => {
          let detailMessage: string = "";
          if (errors.error.startsWith("Incorrect MacroGroup File Format In")) {
            const errorSplit = errors.error.split(": ");
            detailMessage = this.translate.instant(errorSplit[0]) + errorSplit[1];
          }
          this.primeMessageService.add({
            sticky: true,
            severity: 'error',
            summary: this.translate.instant('Modal.Error'),
            detail: detailMessage != "" ? detailMessage : this.translate.instant(errors.error)
          });
        }});
  }

  ngOnDestroy() {
    // prevent memory leak when component destroyed
    this.alive = false;
  }

}
